import React from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text } from "@xsolla/xui-primitives";
import { useResolvedTheme } from "@xsolla/xui-core";
import type {
  ShopCardPlatform,
  ShopCardProps,
  ShopCardStatus,
  ShopCardTagData,
} from "./types";

const sizeConfig: Record<
  ShopCardPlatform,
  {
    width: number;
    minWidth: number;
    paddingTop: number;
    paddingX: number;
    paddingBottom: number;
    imageSize: number;
    imageAreaHeight: number;
    titleFontSize: number;
    titleLineHeight: number;
    buttonHeight: number;
  }
> = {
  mobile: {
    width: 170,
    minWidth: 140,
    paddingTop: 36,
    paddingX: 8,
    paddingBottom: 8,
    imageSize: 120,
    imageAreaHeight: 122,
    titleFontSize: 14,
    titleLineHeight: 18,
    buttonHeight: 32,
  },
  desktop: {
    width: 200,
    minWidth: 180,
    paddingTop: 40,
    paddingX: 16,
    paddingBottom: 16,
    imageSize: 120,
    imageAreaHeight: 136,
    titleFontSize: 16,
    titleLineHeight: 20,
    buttonHeight: 40,
  },
};

const getDefaultActionLabel = (status: ShopCardStatus) => {
  if (status === "free") return "Claim for free";
  if (status === "redeem") return "Redeem";
  if (status === "owned") return "Owned";
  return "$2.50";
};

const Tag: React.FC<{
  tag: ShopCardTagData;
  tone: "primary" | "success";
  theme: ReturnType<typeof useResolvedTheme>["theme"];
}> = ({ tag, tone, theme }) => {
  const isSuccess = tone === "success";

  return (
    <Box
      alignItems="center"
      justifyContent="center"
      paddingHorizontal={6}
      paddingVertical={2}
      borderRadius={4}
      borderWidth={1}
      borderColor={
        isSuccess ? theme.colors.border.success : theme.colors.border.secondary
      }
      backgroundColor={
        isSuccess
          ? theme.colors.background.success.primary
          : theme.colors.overlay.mono
      }
    >
      <Text
        color={
          isSuccess
            ? theme.colors.content.on.success
            : theme.colors.content.primary
        }
        fontFamily={theme.fonts.body}
        fontSize={12}
        lineHeight={14}
        fontWeight="500"
        numberOfLines={1}
      >
        {tag.label}
      </Text>
    </Box>
  );
};

export const ShopCard: React.FC<ShopCardProps> = ({
  title,
  imageSrc,
  imageAlt = "",
  imageFit = "contain",
  imageVisible = true,
  imageFallback,
  glowSrc,
  glowVisible = true,
  leftTags = [],
  rightTags = [],
  status = "default",
  state = "default",
  platform = "mobile",
  actionLabel,
  actionSubLabel,
  onActionPress,
  onPress,
  disabled,
  "aria-label": ariaLabel,
  testID,
  themeMode,
  themeProductContext = "b2c",
}) => {
  const { theme } = useResolvedTheme({ themeMode, themeProductContext });
  const config = sizeConfig[platform];
  const isOwned = status === "owned";
  const isDisabled = disabled || state === "disabled" || isOwned;
  const isInteractive = Boolean(onPress) && !isDisabled;
  const isActionInteractive = Boolean(onActionPress) && !isDisabled;
  const resolvedActionLabel = actionLabel ?? getDefaultActionLabel(status);
  const cardBackground =
    state === "press"
      ? theme.colors.overlay.brand || theme.colors.overlay.mono
      : theme.colors.overlay.mono;
  const actionBackground = isDisabled
    ? theme.colors.overlay.mono
    : theme.colors.background.brand.primary;

  return (
    <Box
      width={config.width}
      minWidth={config.minWidth}
      flexDirection="column"
      alignItems="center"
      paddingTop={config.paddingTop}
      borderRadius={8}
      borderWidth={1}
      borderStyle="solid"
      borderColor={theme.colors.border.secondary}
      backgroundColor={cardBackground}
      position="relative"
      overflow="hidden"
      cursor={isInteractive ? "pointer" : undefined}
      role={isInteractive ? "button" : "group"}
      aria-label={ariaLabel}
      aria-disabled={isDisabled || undefined}
      tabIndex={isInteractive ? 0 : undefined}
      opacity={isDisabled ? 0.72 : 1}
      onPress={isInteractive ? onPress : undefined}
      hoverStyle={
        isInteractive && state === "default"
          ? { backgroundColor: theme.colors.background.secondary }
          : undefined
      }
      pressStyle={{ backgroundColor: theme.colors.overlay.brand }}
      testID={testID || "shop-card"}
    >
      {leftTags.length > 0 && (
        <Box
          position="absolute"
          top={7}
          left={platform === "desktop" ? 15 : 7}
          zIndex={4}
          gap={4}
          alignItems="flex-start"
        >
          {leftTags.map((tag, index) => (
            <Tag
              key={`${String(tag.label)}-${index}`}
              tag={tag}
              tone={tag.tone || "success"}
              theme={theme}
            />
          ))}
        </Box>
      )}
      {rightTags.length > 0 && (
        <Box
          position="absolute"
          top={7}
          right={platform === "desktop" ? 15 : 7}
          zIndex={5}
          gap={4}
          alignItems="flex-end"
        >
          {rightTags.map((tag, index) => (
            <Tag
              key={`${String(tag.label)}-${index}`}
              tag={tag}
              tone={tag.tone || "primary"}
              theme={theme}
            />
          ))}
        </Box>
      )}
      <Box
        width="100%"
        height={config.imageAreaHeight}
        position="relative"
        alignItems="center"
        justifyContent="center"
        paddingHorizontal={24}
      >
        {imageVisible &&
          imageSrc &&
          glowVisible &&
          (glowSrc ? (
            <Box
              as="img"
              src={glowSrc}
              alt=""
              position="absolute"
              width={160}
              height={160}
              style={{ objectFit: "contain" }}
            />
          ) : (
            <Box
              position="absolute"
              width={160}
              height={160}
              borderRadius={999}
              backgroundColor={theme.colors.background.brand.primary}
              opacity={0.12}
            />
          ))}
        {imageVisible && imageSrc ? (
          <Box
            as="img"
            src={imageSrc}
            alt={imageAlt}
            width={config.imageSize}
            height={config.imageSize}
            style={{
              objectFit: imageFit,
              pointerEvents: "none",
            }}
          />
        ) : (
          <Box
            width={48}
            height={48}
            alignItems="center"
            justifyContent="center"
            opacity={0.72}
          >
            {imageFallback}
          </Box>
        )}
      </Box>
      <Box
        width="100%"
        minHeight={20}
        paddingHorizontal={config.paddingX}
        paddingVertical={platform === "desktop" ? 12 : 8}
      >
        <Text
          color={theme.colors.content.primary}
          fontFamily={theme.fonts.body}
          fontSize={config.titleFontSize}
          lineHeight={config.titleLineHeight}
          fontWeight="500"
          numberOfLines={1}
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Text>
      </Box>
      <Box
        width="100%"
        paddingHorizontal={config.paddingX}
        paddingBottom={config.paddingBottom}
      >
        <Box
          width="100%"
          height={config.buttonHeight}
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
          gap={4}
          borderRadius={4}
          backgroundColor={actionBackground}
          opacity={isDisabled ? 0.48 : 1}
          cursor={isActionInteractive ? "pointer" : undefined}
          role={isActionInteractive ? "button" : undefined}
          aria-label={
            isActionInteractive
              ? `Buy ${typeof title === "string" ? title : "item"}`
              : undefined
          }
          aria-disabled={isDisabled || undefined}
          onPress={isActionInteractive ? onActionPress : undefined}
          pressStyle={{
            backgroundColor: theme.colors.background.brand.secondary,
          }}
          testID="shop-card-action"
        >
          <Text
            color={
              isDisabled
                ? theme.colors.content.secondary
                : theme.colors.content.on.brand
            }
            fontFamily={theme.fonts.body}
            fontSize={12}
            lineHeight={14}
            fontWeight="500"
            numberOfLines={1}
          >
            {resolvedActionLabel}
          </Text>
          {actionSubLabel && (
            <Text
              color={
                isDisabled
                  ? theme.colors.content.secondary
                  : theme.colors.content.on.brand
              }
              fontFamily={theme.fonts.body}
              fontSize={12}
              lineHeight={14}
              fontWeight="500"
              numberOfLines={1}
              style={{ opacity: 0.72 }}
            >
              {actionSubLabel}
            </Text>
          )}
        </Box>
      </Box>
    </Box>
  );
};
